import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules } from '@angular/router';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { demoBackendInterceptor } from './core/interceptors/demo-backend.interceptor';
import { quotaErrorInterceptor } from './core/interceptors/quota-error.interceptor';
import { globalErrorInterceptor } from './core/interceptors/global-error.interceptor';
import { MatIconModule, MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { AuthService } from './core/services/auth.service';
import { RemoteFlagsService } from './core/services/remote-flags.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, 
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([demoBackendInterceptor, authInterceptor, quotaErrorInterceptor, globalErrorInterceptor])
    ),
    provideAnimations(),
    importProvidersFrom(MatIconModule),
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: {
        fontSet: 'material-symbols-outlined'
      }
    },
    {
      // One consistent home for every toast in the app: bottom-right, 5s, design-system
      // styled. Existing MatSnackBar.open() calls inherit this without any change; the
      // ToastService layers semantic success/error/info on top. (See styles.scss.)
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
        panelClass: 'dr-snackbar'
      }
    },
    {
      // Angular Material defaults to `appearance: 'fill'` (a grey filled box) when a
      // <mat-form-field> doesn't set one explicitly. A few forms (connection-wizard,
      // job-builder) relied on that default and rendered raw/unthemed inputs while the
      // rest of the app uses `outline` — the tokens in styles.scss only tune outline.
      // Setting the default here means every field is on-brand even if a template omits
      // `appearance="outline"`.
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' }
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AuthService, RemoteFlagsService],
      useFactory: (authService: AuthService, remoteFlags: RemoteFlagsService) => {
        return () => {
          // remoteFlags reacts to auth.user() via an effect() — it must be armed
          // before loadUser()'s async response can set that signal, not after.
          remoteFlags.bootstrapWhenReady();
          authService.loadUser();
        };
      }
    }
  ]
};
